import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { LinkContainer } from "react-router-bootstrap";
import { Container, Nav, Navbar, NavDropdown } from "react-bootstrap";
import axios from "../axios";
import { logout } from "../actions/userActions";
import { themes, useThemePreview } from "../themePreview";

const profileLinks = [
  { section: "profile", label: "Profile", icon: "fa-user" },
  { section: "login", label: "Login", icon: "fa-key" },
  { section: "garage", label: "Garage", icon: "fa-car" },
  { section: "events", label: "Past events", icon: "fa-clock" },
];

const siteLinks = [
  { to: { pathname: "/company-information", search: "?section=purpose" }, label: "About Us", page: "about" },
  { to: "/show", label: "The Show" },
  { to: "/awards", label: "Awards", page: "awards" },
  { to: { pathname: "/awards", search: "?section=entry" }, label: "Rules", page: "rules" },
  { to: { pathname: "/company-information", search: "?section=contact" }, label: "Contact Us", page: "contact" },
];

function pageActive(page) {
  return (match, location) => {
    if (page === "about" || page === "contact") {
      if (location.pathname !== "/company-information") {
        return false;
      }
      const section = new URLSearchParams(location.search).get("section") || "purpose";
      return page === "contact" ? section === "contact" : section !== "contact";
    }
    if (location.pathname !== "/awards") {
      return false;
    }
    const section = new URLSearchParams(location.search).get("section");
    const rules = section === "entry" || section === "judging";
    return page === "rules" ? rules : !rules;
  };
}

function SiteNavLinks() {
  return siteLinks.map((item) => (
    <LinkContainer key={item.label} to={item.to} {...(item.page ? { isActive: pageActive(item.page) } : {})}>
      <Nav.Link>{item.label}</Nav.Link>
    </LinkContainer>
  ));
}

function profileSectionActive(section) {
  return (match, location) => {
    if (!match) {
      return false;
    }
    const current = new URLSearchParams(location.search).get("section") || "profile";
    return current === section;
  };
}

function AccountNav({ userInfo, logoutHandler, menuId, checkout, onCheckout }) {
  const hasCart = (checkout?.events || []).length > 0;
  const due = Number(checkout?.balance || 0) > 0;
  const cartLabel = hasCart ? "Cart" : "Cart is empty";
  return (
    <Nav className="site-account">
      {userInfo ? (
        <button
          type="button"
          className={due ? "checkout-link is-due" : hasCart ? "checkout-link" : "checkout-link is-idle"}
          aria-label={cartLabel}
          title={cartLabel}
          disabled={!hasCart}
          onClick={onCheckout}
        >
          <i className="fa-solid fa-bag-shopping" aria-hidden="true"></i>
        </button>
      ) : null}
      {userInfo ? (
        <NavDropdown title={userInfo.name} id={`${menuId}-user`} align="end">
          {profileLinks.map((item) => (
            <LinkContainer
              key={item.section}
              to={{ pathname: "/profile", search: `?section=${item.section}` }}
              isActive={profileSectionActive(item.section)}
            >
              <NavDropdown.Item>
                <i className={`fa-solid ${item.icon} me-2`}></i>
                {item.label}
              </NavDropdown.Item>
            </LinkContainer>
          ))}
          <NavDropdown.Divider />
          <NavDropdown.Item onClick={logoutHandler}>
            <i className="fa-solid fa-right-from-bracket me-2"></i>
            Logout
          </NavDropdown.Item>
        </NavDropdown>
      ) : (
        <LinkContainer to="/login">
          <Nav.Link>Sign in</Nav.Link>
        </LinkContainer>
      )}
      {userInfo && userInfo.isAdmin && (
        <NavDropdown title="Admin" id={`${menuId}-admin`} align="end">
          <LinkContainer to="/admin/dashboard">
            <NavDropdown.Item>Dashboard</NavDropdown.Item>
          </LinkContainer>
          <LinkContainer to="/admin/userList">
            <NavDropdown.Item>View Users</NavDropdown.Item>
          </LinkContainer>
        </NavDropdown>
      )}
      <Link className="volunteer-mark" to="/company-information?section=volunteers">
        <span>Volunteers</span>
        <strong>Wanted</strong>
      </Link>
      <a
        className="donate-mark"
        href="https://www.paypal.com/donate/?hosted_button_id=RK7LZ4HM2NEBA"
        target="_blank"
        rel="noreferrer"
      >
        <span>Please</span>
        <strong>DONATE</strong>
      </a>
    </Nav>
  );
}

function SandHeader({ userInfo, logoutHandler, checkout, onCheckout }) {
  return (
    <Navbar expand="lg" className="site-nav" collapseOnSelect>
      <Container>
        <LinkContainer to="/">
          <Navbar.Brand className="site-brand">
            <span className="brand-mark">HB</span>
            <span className="brand-words">
              <strong>Huntington Beach</strong>
              <small>Concours d'Elegance</small>
            </span>
          </Navbar.Brand>
        </LinkContainer>
        <Navbar.Toggle aria-controls="site-nav-sand" />
        <Navbar.Collapse id="site-nav-sand">
          <Nav className="mx-auto site-links">
            <SiteNavLinks />
          </Nav>
          <AccountNav
            userInfo={userInfo}
            logoutHandler={logoutHandler}
            menuId="sand"
            checkout={checkout}
            onCheckout={onCheckout}
          />
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

function OceanHeader({ userInfo, logoutHandler, checkout, onCheckout }) {
  return (
    <Navbar expand="lg" variant="dark" className="site-nav site-nav-ocean" collapseOnSelect>
      <Container>
        <LinkContainer to="/">
          <Navbar.Brand className="site-brand ocean-brand">
            <span className="brand-words">
              <strong>Huntington Beach</strong>
              <small>Concours d'Elegance</small>
            </span>
          </Navbar.Brand>
        </LinkContainer>
        <Navbar.Toggle aria-controls="site-nav-ocean" />
        <Navbar.Collapse id="site-nav-ocean">
          <Nav className="mx-auto site-links">
            <SiteNavLinks />
          </Nav>
          <AccountNav
            userInfo={userInfo}
            logoutHandler={logoutHandler}
            menuId="ocean"
            checkout={checkout}
            onCheckout={onCheckout}
          />
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

function ProgramHeader({ userInfo, logoutHandler, checkout, onCheckout }) {
  return (
    <div className="site-nav-program">
      <Container>
        <div className="program-meta">
          <span>June 6–7, 2026 · Central Park</span>
          <span>Huntington Beach</span>
        </div>
        <Navbar expand="lg" className="program-bar" collapseOnSelect>
          <LinkContainer to="/">
            <Navbar.Brand className="program-title">Huntington Beach Concours</Navbar.Brand>
          </LinkContainer>
          <Navbar.Toggle aria-controls="site-nav-program" />
          <Navbar.Collapse id="site-nav-program">
            <Nav className="site-links program-links">
              <SiteNavLinks />
            </Nav>
            <AccountNav
              userInfo={userInfo}
              logoutHandler={logoutHandler}
              menuId="program"
              checkout={checkout}
              onCheckout={onCheckout}
            />
          </Navbar.Collapse>
        </Navbar>
      </Container>
    </div>
  );
}

function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const { template, chooseTemplate } = useThemePreview();
  const [checkout, setCheckout] = useState(null);

  const logoutHandler = () => {
    dispatch(logout());
  };

  useEffect(() => {
    if (!userInfo?.token) {
      setCheckout(null);
      return undefined;
    }
    let cancel = false;
    const load = () => {
      axios
        .get("/api/users/checkout/", { headers: { Authorization: `Bearer ${userInfo.token}` } })
        .then(({ data }) => {
          if (!cancel) {
            setCheckout(data?.events?.length ? data : null);
          }
        })
        .catch(() => {
          if (!cancel) {
            setCheckout(null);
          }
        });
    };
    load();
    window.addEventListener("hbc-checkout", load);
    window.addEventListener("focus", load);
    return () => {
      cancel = true;
      window.removeEventListener("hbc-checkout", load);
      window.removeEventListener("focus", load);
    };
  }, [userInfo]);

  const onCheckout = () => {
    if (!(checkout?.events || []).length) {
      return;
    }
    navigate("/cart");
  };

  const headerProps = { userInfo, logoutHandler, checkout, onCheckout };

  return (
    <header>
      <div className="template-switch">
        <span>Look preview</span>
        {Object.values(themes).map((item) => (
          <button
            key={item.id}
            type="button"
            className={template === item.id ? "is-on" : ""}
            onClick={() => chooseTemplate(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {template === "ocean" && <OceanHeader {...headerProps} />}
      {template === "program" && <ProgramHeader {...headerProps} />}
      {template !== "ocean" && template !== "program" && <SandHeader {...headerProps} />}
    </header>
  );
}

export default Header;
